<template>
    <div class="app-code-editor"
        :class="{ 'has--line-numbers': showCssLineNumbers }">
        <div class="app-code-editor__code">
            <div
                class="app-code-editor__editor-wrap">
                <div
                    v-if="showCssLineNumbers"
                    ref="lineNumbers"
                    class="app-code-editor__line-numbers"
                    aria-hidden="true">
                    <div
                        class="app-code-editor__line-numbers-content"
                        :style="lineNumberTransformStyle">
                        <span
                            v-for="lineNumber in editorLineNumbers"
                            :key="`editor-line-${lineNumber}`"
                            class="app-code-editor__line-number">{{ lineNumber }}</span>
                    </div>
                </div>
            <textarea
                ref="editor"
                :class="{ 'is--invalid': validationErrors.length }"
                v-model="internalValue"
                @keydown="checkKeys"
                @input="onInputDelayed($event)"
                :rows="rows"
                :cols="cols"
                :placeholder="placeholder"
                :maxlength="enableCharacterLimit ? editorMaxCharacterLimit : null"
                @change="$emit('change', $event)">
            </textarea>
            </div>
        </div>

        <div class="app-code-editor__errors"
            v-if="validationErrors.length">
            <ul>
                <li class="inline-message is--error" v-for="(error, index) in validationErrors" :key="`error-${index}`">Validation Error: {{ error.message }}</li>
            </ul>
        </div>
        <p v-if="enableCharacterLimit" class="element-character-count">
            Character count: {{ getFieldCharacterCount() }}
        </p>
    </div>
</template>

<script>
import _ from 'lodash'
import validators from '../helpers/validators'

export default {
    props: {
        value: {
            type: String,
            default: ''
        },
        language: {
            type: String,
            default: 'scss'
        },
        validation: {
            type: Boolean,
            default: true
        },
        enableCharacterLimit: {
            type: Boolean,
            default: true
        },
        rows: [Number, String],
        cols: [Number, String],
        placeholder: [String]
    },
    data () {
        return {
            internalValue: this.value || '',
            validationErrors: [],
            selectionStart: 0,
            selectionEnd: 0,
            lineNumberOffset: 0,
            onInputDelayed: null,
            editorMaxCharacterLimit:250000
        }
    },
    watch: {
        value (value) {
            this.internalValue = value
            this.syncLineNumberScroll()
        }
    },
    computed: {
        showCssLineNumbers () {
            return this.language === 'css'
        },
        minimumVisibleLines () {
            const parsedRows = Number(this.rows)
            return Number.isFinite(parsedRows) && parsedRows > 0 ? Math.floor(parsedRows) : 1
        },
        editorLineNumbers () {
            const contentLines = typeof this.internalValue === 'string' && this.internalValue.length
                ? this.internalValue.split(/\r?\n/).length
                : 1

            const totalLines = Math.max(contentLines, this.minimumVisibleLines)

            return Array.from({ length: totalLines }, (_, index) => index + 1)
        },
        lineNumberTransformStyle () {
            return {
                transform: `translateY(-${this.lineNumberOffset}px)`
            }
        }
    },
    methods: {
        reset () {
            this.internalValue = this.value
            this.editorCurrentCharacterCount = this.internalValue.length
        },
        checkKeys (e) {
            const keyCode = e.keyCode || e.which

            // capture tab key
            if (keyCode === 9) {
                e.preventDefault()
                this.insert('  ')
            }
        },
        getValue () {
            return this.internalValue
        },
        insert (value) {
            this.$refs['editor'].focus()
            document.execCommand('insertText', false, value);
        },
        saveSelection (e) {
            this.selectionStart = this.$refs['editor'].selectionStart
            this.selectionEnd = this.$refs['editor'].selectionEnd
        },
        restoreSelection (e) {
            this.$refs['editor'].setSelectionRange(this.selectionStart, this.selectionEnd)
        },
        clearErrors () {
            this.validationErrors = []
        },
        getFieldCharacterCount () {
            let str = this.internalValue.length.toLocaleString()  + '/' + this.editorMaxCharacterLimit.toLocaleString()
            return str
        },
        syncLineNumberScroll () {
            if (!this.showCssLineNumbers || !this.$refs['lineNumbers'] || !this.$refs['editor']) {
                return
            }

            this.lineNumberOffset = this.$refs['editor'].scrollTop
        },
        async onInput (value, triggerValidation = false) {
            this.clearErrors()

            if (this.validation && (this.language === 'scss' || this.language === 'json') || triggerValidation) {
                const validatorFn = validators[this.language]
                if (_.isFunction(validatorFn)) {
                    const { success, error } = validatorFn(this.internalValue)

                    if (!success) {
                        console.error(error)
                        this.$refs['editor'].focus()
                        const errorData = Array.isArray(error) ? error : [error]
                        this.validationErrors = errorData
                        this.$emit('validationError', errorData)
                        return false
                    }
                    else {
                        this.$emit('validationError', null)
                        return true
                    }
                }
            }
            this.$emit('inputText', value)
        }
    },
    created () {
        this.onInputDelayed = _.debounce(this.onInput, 300)
    },
    mounted () {
        this.$refs['editor'].addEventListener('blur', this.saveSelection)
        this.$refs['editor'].addEventListener('focus', this.restoreSelection)
        this.$refs['editor'].addEventListener('scroll', this.syncLineNumberScroll)
        this.syncLineNumberScroll()
    },
    beforeUnmount () {
        this.$refs['editor'].removeEventListener('scroll', this.syncLineNumberScroll)
        this.$refs['editor'].removeEventListener('blur', this.saveSelection)
        this.$refs['editor'].removeEventListener('focus', this.restoreSelection)
    }
}
</script>